import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, switchMap } from 'rxjs';
import { WIDGET_DASHBOARD_ID_HEADER } from 'scw-digital-boards-widgets';
import { TReducerState } from '../../../../../app.config';
import { ELoadStatus } from '../../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../utilities/http-utilities.service';
import { IWidgetRequest } from '../../../../settings/data-integrations/widget-requests/widget-requests.interface';
import { IForms } from '../../widget-add-edit-dialog.interface';

export interface IWidgetRequestDropdownState {
  widgetRequest: IWidgetRequest[];
  widgetRequestLoadStatus: ELoadStatus;
  forms: IForms[];
  formsLoadStatus: ELoadStatus;
}

@Injectable()
export class WidgetRequestDropdownStore extends ComponentStore<IWidgetRequestDropdownState> {
  private readonly http = inject(HttpClient);
  private readonly httpUtilities = inject(HttpUtilitiesService);
  private readonly globalStore = inject<Store<TReducerState>>(Store);
  private readonly dashboardId = this.globalStore.selectSignal(
    ({ dashboardContent }) => dashboardContent.dashboardData?.id,
  );
  public readonly formsLoadStatus = this.selectSignal((state) => state.formsLoadStatus);
  public readonly widgetRequestsLoadStatus = this.selectSignal((state) => state.widgetRequestLoadStatus);
  public readonly widgetRequests = this.selectSignal((state) => state.widgetRequest);
  public readonly forms = this.selectSignal((state) => state.forms);
  public readonly widgetRequestsAreLoading = computed(
    () =>
      this.widgetRequestsLoadStatus() === ELoadStatus.loading ||
      this.widgetRequestsLoadStatus() === ELoadStatus.initialLoad,
  );
  public readonly formsAreLoading = computed(
    () => this.formsLoadStatus() === ELoadStatus.loading || this.formsLoadStatus() === ELoadStatus.initialLoad,
  );

  public readonly loadCustomWidgetRequests = this.effect<IGenericCrudRequestConstructionParameters>((trigger$) => {
    return trigger$.pipe(
      switchMap((crudRequestParams: IGenericCrudRequestConstructionParameters) => {
        const params: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(crudRequestParams);
        this.patchState(({ widgetRequestLoadStatus }) => ({
          widgetRequestLoadStatus:
            widgetRequestLoadStatus === ELoadStatus.initial ? ELoadStatus.initialLoad : ELoadStatus.loading,
        }));

        return this.getWidgetRequests({ params }).pipe(
          tapResponse(
            ({ data }) => {
              this.patchState({
                widgetRequest: data,
                widgetRequestLoadStatus: ELoadStatus.success,
              });
            },
            (_) => {
              this.patchState({
                widgetRequestLoadStatus: ELoadStatus.failure,
              });
            },
          ),
        );
      }),
    );
  });

  public readonly loadForms = this.effect<IGenericCrudRequestConstructionParameters>((trigger$) => {
    return trigger$.pipe(
      switchMap((crudRequestParams: IGenericCrudRequestConstructionParameters) => {
        const params: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(crudRequestParams);
        this.patchState(({ widgetRequestLoadStatus }) => ({
          formsLoadStatus:
            widgetRequestLoadStatus === ELoadStatus.initial ? ELoadStatus.initialLoad : ELoadStatus.loading,
        }));

        return this.getForms({ params }).pipe(
          tapResponse(
            ({ data }) => {
              this.patchState({
                forms: data,
                formsLoadStatus: ELoadStatus.success,
              });
            },
            (_) => {
              this.patchState({
                formsLoadStatus: ELoadStatus.failure,
              });
            },
          ),
        );
      }),
    );
  });

  public getWidgetRequests(
    options: Parameters<HttpClient['get']>[1] = {},
  ): Observable<IGetManyResponse<IWidgetRequest>> {
    const dashboardId: number | undefined = this.dashboardId();

    return this.http.get<IGetManyResponse<IWidgetRequest>>('widget-requests', {
      ...options,
      headers: {
        ...(options.headers as Record<string, string | string[]> | undefined),
        ...(dashboardId ? { [WIDGET_DASHBOARD_ID_HEADER]: String(dashboardId) } : {}),
      },
    });
  }

  public getForms(options: Parameters<HttpClient['get']>[1] = {}): Observable<IGetManyResponse<IForms>> {
    return this.http.get<IGetManyResponse<IForms>>('forms', {
      ...options,
    });
  }

  constructor() {
    super({
      forms: [],
      formsLoadStatus: ELoadStatus.initial,
      widgetRequest: [],
      widgetRequestLoadStatus: ELoadStatus.initial,
    });
  }
}
